// tags behaviour — cleanSlate's zero-dep multi-select tag input.
//
// base selectMultiple delegates its tag widget to hm.behaviors.tags.create
// (metronic_8 wraps Tagify; cleanSlate rolls its own here, keeping the theme
// dependency-free). The widget enhances the selectMultiple text input in
// place: it hides the original input (kept in the DOM so its value still
// submits and the framework component can read it), and builds a chips control
// plus a filtered options dropdown. The dropdown reuses the submenu adapter for
// fixed positioning (escaping the window card's overflow:hidden) and the
// outside-click / page-scroll close lifecycle — the same panel mechanics the
// searchable select uses.
//
// Contract: create(el, { options, freeTags }) -> { getValues, selectAll, selectNone }.
// The component passes only its option list + free-tagging flag; this adapter
// owns all the widget construction and keeps el.value as the comma-joined ids.

import submenu from './submenu.js';

// Build the widget on one selectMultiple input element.
function createTagsInstance(el, config) {
    // Normalise option ids to strings (selection comparison is string-based;
    // el.value is always a string).
    const options = (config.options || []).map((option) => ({
        id: String(option.id),
        name: option.name
    }));
    const freeTags = !!config.freeTags;

    // Current selection: array of id strings, seeded from the input's
    // comma-joined value (the server-rendered initial selection).
    let selected = String(el.value || '')
        .split(',')
        .map((value) => value.trim())
        .filter((value) => value !== '');

    // --- DOM scaffold ----------------------------------------------------
    // wrapper > (control > chips + typing input) + (menu > options list).
    const wrapper = document.createElement('div');
    wrapper.className = 'zit-cs-tags';

    const control = document.createElement('div');
    control.className = 'zit-cs-tags-control';

    // The inline text input: filters the options and (in freeTags mode) accepts
    // typed tags. It carries no name attribute, so it never submits — only the
    // hidden source input does.
    const typeInput = document.createElement('input');
    typeInput.type = 'text';
    typeInput.className = 'zit-cs-tags-input';
    typeInput.autocomplete = 'off';
    if (el.placeholder) {
        typeInput.placeholder = el.placeholder;
    }
    control.appendChild(typeInput);

    const menu = document.createElement('div');
    menu.className = 'zit-cs-tags-menu';
    // data-zit-menu marks this as a submenu panel the adapter can manage.
    menu.setAttribute('data-zit-menu', 'true');

    const optionsHolder = document.createElement('div');
    optionsHolder.className = 'zit-cs-tags-options';
    menu.appendChild(optionsHolder);

    wrapper.appendChild(control);
    wrapper.appendChild(menu);

    // Hide the original input (display:none !important via the base utility) but
    // keep it in the DOM for the form value, then insert the widget after it.
    el.classList.add('zit-display-none');
    el.parentNode.insertBefore(wrapper, el.nextSibling);

    // --- dropdown (submenu) instance ------------------------------------
    // createInstances is idempotent; get() returns the instance for our menu.
    submenu.createInstances();
    const dropdown = submenu.get(menu);

    // Display name for an id (a free tag has no option, so it labels itself).
    const nameFor = function (id) {
        for (let i = 0; i < options.length; i++) {
            if (options[i].id === id) {
                return options[i].name;
            }
        }
        return id;
    };

    // Mirror the selection back onto the hidden source input and notify the
    // framework (dependables / change listeners read the input value).
    const syncSource = function () {
        el.value = selected.join(',');
        el.dispatchEvent(new Event('change'));
    };

    // Re-render the chips (everything before the typing input in the control).
    const renderChips = function () {
        const existing = control.querySelectorAll('.zit-cs-tags-chip');
        existing.forEach((chip) => chip.remove());

        selected.forEach((id) => {
            const chip = document.createElement('span');
            chip.className = 'zit-cs-tags-chip';

            const label = document.createElement('span');
            label.className = 'zit-cs-tags-chip-label';
            label.textContent = nameFor(id);
            chip.appendChild(label);

            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'zit-cs-tags-remove';
            remove.setAttribute('aria-label', 'remove');
            remove.textContent = '×'; // ×
            remove.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                removeTag(id);
            });
            chip.appendChild(remove);

            control.insertBefore(chip, typeInput);
        });
    };

    // Re-render the options list, filtered by the typed query, marking the
    // already-selected options active.
    const renderOptions = function (filter) {
        optionsHolder.innerHTML = '';
        const query = String(filter || '').toLowerCase();

        options.forEach((option) => {
            if (query !== '' && option.name.toLowerCase().indexOf(query) === -1) {
                return;
            }

            const optionLink = document.createElement('a');
            optionLink.href = '#';
            optionLink.className = 'zit-cs-tags-option';
            optionLink.setAttribute('data-opt-id', option.id);
            optionLink.textContent = option.name;
            if (selected.indexOf(option.id) !== -1) {
                optionLink.classList.add('zit-cs-active');
            }
            optionLink.addEventListener('click', (e) => {
                e.preventDefault();
                toggleTag(option.id);
                typeInput.focus();
            });
            optionsHolder.appendChild(optionLink);
        });
    };

    const addTag = function (id) {
        if (selected.indexOf(id) === -1) {
            selected.push(id);
            renderChips();
            syncSource();
        }
    };

    const removeTag = function (id) {
        const pos = selected.indexOf(id);
        if (pos !== -1) {
            selected.splice(pos, 1);
            renderChips();
            syncSource();
            renderOptions(typeInput.value);
        }
    };

    const toggleTag = function (id) {
        if (selected.indexOf(id) === -1) {
            addTag(id);
        } else {
            removeTag(id);
        }
        renderOptions(typeInput.value);
    };

    // --- events ----------------------------------------------------------
    // Clicking anywhere in the control focuses the typing input and opens the
    // dropdown (anchored to the control so the submenu positions under it).
    control.addEventListener('click', () => {
        typeInput.focus();
        dropdown.show(control);
    });

    // Typing filters the options and keeps the dropdown open.
    typeInput.addEventListener('input', () => {
        renderOptions(typeInput.value);
        dropdown.show(control);
    });

    typeInput.addEventListener('keydown', (e) => {
        if (e.key === 'Backspace' && typeInput.value === '' && selected.length > 0) {
            // Backspace on an empty field removes the last chip.
            removeTag(selected[selected.length - 1]);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const typed = typeInput.value.trim();
            if (typed === '') {
                return;
            }

            // Match the typed text to an option by name (case-insensitive).
            let matchedId = null;
            for (let i = 0; i < options.length; i++) {
                if (options[i].name.toLowerCase() === typed.toLowerCase()) {
                    matchedId = options[i].id;
                    break;
                }
            }

            if (matchedId !== null) {
                addTag(matchedId);
            } else if (freeTags) {
                // A free tag's value is the typed text itself.
                addTag(typed);
            }

            typeInput.value = '';
            renderOptions('');
        }
    });

    // Reset the filter when the dropdown closes.
    dropdown.on('hidden', () => {
        typeInput.value = '';
        renderOptions('');
    });

    // --- initial paint ---------------------------------------------------
    renderChips();
    renderOptions('');

    // --- contract handle -------------------------------------------------
    return {
        // Current selection as an array of value strings.
        getValues: function () {
            return selected.slice();
        },
        // Select every whitelisted option (free tags already typed are kept).
        selectAll: function () {
            options.forEach((option) => {
                if (selected.indexOf(option.id) === -1) {
                    selected.push(option.id);
                }
            });
            renderChips();
            syncSource();
            renderOptions(typeInput.value);
        },
        // Clear the selection.
        selectNone: function () {
            selected = [];
            renderChips();
            syncSource();
            renderOptions(typeInput.value);
        }
    };
}

const tags = {
    create: function (el, config) {
        return createTagsInstance(el, config);
    }
};

export default tags;
